import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';

const API = process.env.REACT_APP_API_URL || 'https://web-production-120c.up.railway.app';

const s = {
  page: { padding: '20px 16px', overflowY: 'auto', flex: 1 },
  greeting: { fontSize: 18, fontWeight: 500, color: 'var(--text-primary)', marginBottom: 2 },
  sub: { fontSize: 12, color: 'var(--text-muted)', marginBottom: 20 },
  metrics: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 10, marginBottom: 20 },
  metric: { background: 'var(--bg-surface)', border: '0.5px solid var(--border)', borderRadius: 10, padding: '14px' },
  mLabel: { fontSize: 10, color: 'var(--text-muted)', letterSpacing: '0.06em', marginBottom: 6 },
  mValue: { fontSize: 26, fontWeight: 500 },
  mSub: { fontSize: 11, color: 'var(--text-muted)', marginTop: 2 },
  card: { background: 'var(--bg-surface)', border: '0.5px solid var(--border)', borderRadius: 10, padding: '16px', marginBottom: 12 },
  cardTitle: { fontSize: 11, color: 'var(--text-muted)', letterSpacing: '0.06em', marginBottom: 12 },
  alertRow: { display: 'flex', gap: 10, alignItems: 'flex-start', padding: '9px 0', borderBottom: '0.5px solid var(--bg-raised)' },
  alertDot: { width: 7, height: 7, borderRadius: '50%', flexShrink: 0, marginTop: 5 },
  alertText: { fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, flex: 1 },
  alertTime: { fontSize: 11, color: 'var(--text-muted)', marginTop: 2 },
  quickBtn: { display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', background: 'var(--bg-raised)', border: '0.5px solid var(--border)', borderRadius: 9, cursor: 'pointer', marginBottom: 8, width: '100%', textAlign: 'left', transition: 'border-color 0.1s' },
  quickLabel: { fontSize: 13, color: 'var(--text-secondary)', flex: 1 },
  quickArrow: { fontSize: 16, color: 'var(--text-muted)' },
};

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
}

const QUICK_ACTIONS = [
  { label: 'Run a digital exposure check', path: '/chat', msg: 'Run a digital exposure check on me' },
  { label: 'Request a security briefing', path: '/chat', msg: 'Generate a personal security briefing for me' },
  { label: 'Travel security advisory', path: '/travel', msg: null },
  { label: 'View my reports', path: '/reports', msg: null },
];

export default function Dashboard() {
  const { client } = useAuth();
  const nav = useNavigate();
  const [health, setHealth] = useState(null);
  const [riskScore, setRiskScore] = useState(null);
  const [cases, setCases] = useState([]);
  const [banner, setBanner] = useState(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [threatBanner, setThreatBanner] = useState(null);
  const [threatDismissed, setThreatDismissed] = useState(false);
  const [pendingFindings, setPendingFindings] = useState([]);
  const [actingOn, setActingOn] = useState(null);

  useEffect(() => {
    if (!client?.id) return;
    const token = localStorage.getItem('shaheen_token') || '';
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

    Promise.all([
      fetch(`${API}/client/${client.id}/health`, { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API}/client/${client.id}/risk-score/latest`, { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API}/client/${client.id}/cases`, { headers }).then(r => r.json()).catch(() => ({ cases: [] })),
      fetch(`${API}/client/${client.id}/threat-watchlist`, { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
      fetch(`${API}/client/${client.id}/scans`, { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
    ]).then(([h, rs, c, wl, sc]) => {
      if (h?.health_score !== undefined) setHealth(h);
      if (rs?.score !== undefined) {
        setRiskScore(rs);
        const prevKey = `shaheen_prev_score_${client.id}`;
        const prevRaw = localStorage.getItem(prevKey);
        const prev = prevRaw ? parseInt(prevRaw, 10) : null;
        const curr = rs.score;
        if (prev !== null && Math.abs(curr - prev) >= 10) {
          setBanner({ type: curr < prev ? 'drop' : 'rise', from: prev, to: curr });
        }
        localStorage.setItem(prevKey, String(curr));
      }
      setCases(c?.cases || []);
      if (wl?.watchlist?.length > 0) {
        const critical = wl.watchlist.filter(a => a.threat_level === 'CRITICAL');
        if (critical.length > 0) setThreatBanner(critical.length);
      }
      const latestScan = sc?.scans?.[0];
      if (latestScan) {
        const pending = (latestScan.pending_confirmation || []).filter(f => f.status === 'pending');
        setPendingFindings(pending);
      }
    });
  }, [client]);

  const displayScore = riskScore?.score ?? health?.health_score;
  const displayGrade = riskScore?.grade ?? health?.grade;
  const displayStatus = riskScore
    ? (riskScore.score >= 75 ? 'Strong' : riskScore.score >= 60 ? 'Moderate' : 'At risk')
    : health?.status;

  const scoreColor = displayScore !== undefined
    ? (displayScore >= 75 ? 'var(--green)' : displayScore >= 60 ? 'var(--amber)' : 'var(--red)')
    : 'var(--gold)';

  const openCases = cases.filter(c => c.status === 'OPEN');

  const actOnFinding = async (findingId, action) => {
    setActingOn(findingId);
    try {
      const token = localStorage.getItem('shaheen_token') || '';
      await fetch(`${API}/client/${client.id}/findings/${findingId}/${action}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      setPendingFindings(prev => prev.filter(f => f.id !== findingId));
      if (action === 'confirm') {
        // Refresh risk score after confirmation
        fetch(`${API}/client/${client.id}/risk-score/latest`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }).then(r => r.ok ? r.json() : null).then(rs => { if (rs?.score !== undefined) setRiskScore(rs); }).catch(() => {});
      }
    } catch {}
    setActingOn(null);
  };

  return (
    <div style={s.page}>
      <div style={s.greeting}>{greeting()}, {client?.name?.split(' ')[0] || 'there'}</div>
      <div style={s.sub}>{new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' })}</div>

      {/* Score change banner */}
      {banner && !bannerDismissed && (
        <div style={{
          borderRadius: 10, padding: '12px 14px', marginBottom: 16,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
          background: banner.type === 'drop' ? 'rgba(239,68,68,0.08)' : 'rgba(34,197,94,0.08)',
          border: `0.5px solid ${banner.type === 'drop' ? 'var(--red)' : 'var(--green)'}`,
        }}>
          <span style={{ fontSize: 13, color: banner.type === 'drop' ? 'var(--red)' : 'var(--green)', flex: 1, lineHeight: 1.5 }}>
            {banner.type === 'drop'
              ? `Your security score dropped from ${banner.from} to ${banner.to} — tap to see what changed`
              : `Your security score improved from ${banner.from} to ${banner.to}`}
          </span>
          <button
            style={{ fontSize: 11, background: banner.type === 'drop' ? 'var(--red)' : 'var(--green)', color: 'white', border: 'none', borderRadius: 6, padding: '5px 12px', cursor: 'pointer', flexShrink: 0 }}
            onClick={() => { setBannerDismissed(true); nav('/reports'); }}
          >
            View
          </button>
        </div>
      )}

      {/* Critical threat actor banner */}
      {threatBanner && !threatDismissed && (
        <div style={{
          borderRadius: 10, padding: '12px 14px', marginBottom: 16,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10,
          background: 'rgba(239,68,68,0.08)', border: '0.5px solid var(--red)',
        }}>
          <span style={{ fontSize: 13, color: 'var(--red)', flex: 1, lineHeight: 1.5 }}>
            {threatBanner} critical threat actor{threatBanner > 1 ? 's' : ''} actively targeting your industry — tap to review
          </span>
          <button
            style={{ fontSize: 11, background: 'var(--red)', color: 'white', border: 'none', borderRadius: 6, padding: '5px 12px', cursor: 'pointer', flexShrink: 0, marginRight: 6 }}
            onClick={() => { nav('/reports'); }}
          >Review</button>
          <button
            style={{ fontSize: 11, background: 'transparent', color: 'var(--red)', border: 'none', cursor: 'pointer', padding: '0 4px' }}
            onClick={() => setThreatDismissed(true)}
          >✕</button>
        </div>
      )}

      {/* Open cases banner */}
      {openCases.length > 0 && (
        <div style={{ background: 'var(--red-bg)', border: '0.5px solid var(--red)', borderRadius: 10, padding: '12px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ fontSize: 13, color: 'var(--red)' }}>{openCases.length} open case{openCases.length > 1 ? 's' : ''} need attention</span>
          <button style={{ fontSize: 11, background: 'var(--red)', color: 'white', border: 'none', borderRadius: 6, padding: '5px 12px', cursor: 'pointer' }} onClick={() => nav('/incidents')}>View</button>
        </div>
      )}

      {/* Pending findings review section */}
      {pendingFindings.length > 0 && (
        <div style={{ ...s.card, border: '0.5px solid rgba(212,175,55,0.4)', background: 'rgba(212,175,55,0.04)', marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: 'var(--gold)', letterSpacing: '0.07em', fontWeight: 500 }}>
              PENDING YOUR REVIEW
            </div>
            <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
              {pendingFindings.length} finding{pendingFindings.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.7, marginBottom: 14 }}>
            The following items were found during your digital assessment. They require your input before any action is taken.
            Your security score is not affected until you review these. Many will be routine and require no action.
          </div>
          {pendingFindings.map((f, i) => (
            <div key={f.id} style={{
              background: 'var(--bg-raised)', borderRadius: 8, padding: '12px 14px',
              marginBottom: i < pendingFindings.length - 1 ? 8 : 0,
            }}>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', letterSpacing: '0.06em', marginBottom: 5, textTransform: 'uppercase' }}>
                {f.type === 'impersonation' ? 'Name reference online' :
                 f.type === 'social' ? 'Social media presence' :
                 f.type === 'news' ? 'Online mention' : 'Online finding'}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 10 }}>
                {f.description}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => actOnFinding(f.id, 'confirm')}
                  disabled={actingOn === f.id}
                  style={{ flex: 1, padding: '8px', fontSize: 11, fontWeight: 500, background: 'rgba(212,175,55,0.12)', border: '0.5px solid var(--gold)', borderRadius: 7, color: 'var(--gold)', cursor: 'pointer', opacity: actingOn === f.id ? 0.5 : 1 }}
                >
                  This is me
                </button>
                <button
                  onClick={() => actOnFinding(f.id, 'dismiss')}
                  disabled={actingOn === f.id}
                  style={{ flex: 1, padding: '8px', fontSize: 11, background: 'transparent', border: '0.5px solid var(--border)', borderRadius: 7, color: 'var(--text-muted)', cursor: 'pointer', opacity: actingOn === f.id ? 0.5 : 1 }}
                >
                  Not me
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div style={s.metrics}>
        <div style={s.metric}>
          <div style={s.mLabel}>PROTECTION SCORE</div>
          <div style={{ ...s.mValue, color: scoreColor }}>
            {displayScore ?? '—'}
            {displayGrade && <span style={{ fontSize: 14, marginLeft: 6, opacity: 0.8 }}>{displayGrade}</span>}
          </div>
          <div style={s.mSub}>{displayStatus ?? 'One moment...'}</div>
          {(riskScore?.factors?.pending_count > 0) && (
            <div style={{ fontSize: 10, color: 'var(--gold)', marginTop: 4, opacity: 0.85 }}>
              {riskScore.factors.pending_count} finding{riskScore.factors.pending_count !== 1 ? 's' : ''} pending review
            </div>
          )}
        </div>
        <div style={s.metric}>
          <div style={s.mLabel}>MONITORING</div>
          <div style={{ ...s.mValue, fontSize: 16, paddingTop: 4, color: 'var(--green)' }}>Active</div>
          <div style={s.mSub}>24 / 7</div>
        </div>
        <div style={s.metric}>
          <div style={s.mLabel}>OPEN CASES</div>
          <div style={{ ...s.mValue, color: openCases.length > 0 ? 'var(--red)' : 'var(--green)' }}>{openCases.length}</div>
          <div style={s.mSub}>{cases.length} total</div>
        </div>
        <div style={s.metric}>
          <div style={s.mLabel}>LAST ASSESSMENT</div>
          <div style={{ ...s.mValue, fontSize: 14, paddingTop: 4 }}>
            {health?.last_scan ? new Date(health.last_scan).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : 'Never'}
          </div>
          <div style={s.mSub}>{health?.scan_risk_level ? `${health.scan_risk_level} risk` : 'No assessment yet'}</div>
        </div>
      </div>

      <div style={s.card}>
        <div style={s.cardTitle}>QUICK ACTIONS</div>
        {QUICK_ACTIONS.map(a => (
          <button key={a.label} style={s.quickBtn}
            onClick={() => { if (a.msg) { localStorage.setItem('shaheen_prefill', a.msg); } nav(a.path); }}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border-hover)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
            <span style={s.quickLabel}>{a.label}</span>
            <span style={s.quickArrow}>›</span>
          </button>
        ))}
      </div>

      {/* Travel advisory promo card */}
      <div style={{ ...s.card, background: 'linear-gradient(135deg, rgba(200,180,138,0.08) 0%, rgba(200,180,138,0.03) 100%)', border: '0.5px solid rgba(200,180,138,0.25)', cursor: 'pointer' }}
        onClick={() => nav('/travel')}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 28, flexShrink: 0 }}>✈</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--gold)', marginBottom: 3 }}>Travel Security Advisory</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 }}>
              Get a personalised cyber-security briefing before international travel — powered by NCSI data
            </div>
          </div>
          <span style={{ fontSize: 18, color: 'var(--text-muted)' }}>›</span>
        </div>
      </div>

      {cases.length > 0 && (
        <div style={s.card}>
          <div style={s.cardTitle}>RECENT CASES</div>
          {cases.slice(0, 3).map((c, i) => (
            <div key={i} style={{ ...s.alertRow, ...(i === Math.min(cases.length, 3) - 1 ? { borderBottom: 'none' } : {}) }}>
              <div style={{ ...s.alertDot, background: c.severity === 'CRITICAL' ? 'var(--red)' : c.severity === 'HIGH' ? 'var(--amber)' : 'var(--green)' }} />
              <div>
                <div style={s.alertText}>{(c.incident_type || '').replace(/_/g, ' ')}</div>
                <div style={s.alertTime}>{c.status} — {c.created_at?.slice(0, 10)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
